import React, { useState } from 'react';
import {
  AddCircleIcon,
  Button,
  Box,
  CancelIcon,
  EditIcon,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@cherre-frontend/ui';
import { CustomPropertyAttribute } from '../../types/CustomPropertyAttribute';
import { CustomAttributeForm } from './CustomAttributeForm';
import { CustomAttributesList } from './CustomAttributesList';

export interface CurrentCustomAttributesTabProps {
  allAttributes: CustomPropertyAttribute[];
  activeAttributes: CustomPropertyAttribute[];
  onAddAttribute: (attribute: CustomPropertyAttribute) => void;
  onEditAttribute: (slug: string, attribute: CustomPropertyAttribute) => void;
  onArchiveAttribute: (slug: string) => void;
  onMoveAttribute: (fromAttributeSlug: string, toAttributeSlug: string) => void;
  setIsSaveButtonDisabled: (value: boolean) => void;
}

export const CurrentCustomAttributesTab = ({
  allAttributes,
  activeAttributes,
  onAddAttribute,
  onEditAttribute,
  onArchiveAttribute,
  onMoveAttribute,
  setIsSaveButtonDisabled,
}: CurrentCustomAttributesTabProps) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [attributeToEdit, setAttributeToEdit] =
    useState<CustomPropertyAttribute>();

  const setFormVisible = () => {
    setIsFormVisible(true);
    setIsSaveButtonDisabled(true);
  };

  const showEditAttributeForm = (attribute: CustomPropertyAttribute) => {
    setAttributeToEdit(attribute);
    setFormVisible();
  };

  const closeForm = () => {
    setIsFormVisible(false);
    setIsSaveButtonDisabled(false);
    setAttributeToEdit(undefined);
  };

  const saveAttributeAndCloseForm = (attribute: CustomPropertyAttribute) => {
    if (attributeToEdit) {
      onEditAttribute(attributeToEdit.slug, attribute);
    } else {
      onAddAttribute(attribute);
    }
    closeForm();
  };

  if (activeAttributes.length === 0 && !isFormVisible) {
    return (
      <Stack
        padding={2.5}
        gap={1}
        textAlign='center'
        borderRadius='10px'
        border='1px dashed #BDBDBD'
      >
        <Typography fontWeight={700}>No Custom Attributes Added</Typography>
        <Typography fontWeight={400}>
          Click the button below to add your first custom property attribute.
        </Typography>
        <Button
          variant='text'
          onClick={setFormVisible}
          startIcon={<AddCircleIcon />}
        >
          Add Attribute
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <CustomAttributesList
        attributes={activeAttributes}
        disableReorder={isFormVisible}
        onMoveAttribute={onMoveAttribute}
        renderActions={(attribute) => (
          <>
            <Tooltip title='Edit'>
              <IconButton
                size='small'
                color='primary'
                disabled={isFormVisible}
                onClick={() => showEditAttributeForm(attribute)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Archive Attribute'>
              <IconButton
                size='small'
                color='primary'
                disabled={attributeToEdit === attribute}
                onClick={() => onArchiveAttribute(attribute.slug)}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      />

      <Box
        flex='0 0 auto'
        position='sticky'
        bottom='56.5px'
        sx={{ backgroundColor: 'white' }}
      >
        {isFormVisible ? (
          <CustomAttributeForm
            allAttributes={allAttributes}
            attributeToEdit={attributeToEdit}
            onSave={saveAttributeAndCloseForm}
            onCancel={closeForm}
          />
        ) : (
          <Button
            variant='text'
            onClick={setFormVisible}
            startIcon={<AddCircleIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Attribute
          </Button>
        )}
      </Box>
    </>
  );
};
